/**
 * CalculatorDisplay - Retro LCD display for calculator
 *
 * Features:
 * - True seven-segment LCD digits (stick/bar style)
 * - Fixed number of digit slots
 * - AZN currency symbol at the end
 */

import { cn } from '@/lib/utils';

// ============================================
// TYPES
// ============================================

export interface CalculatorDisplayProps {
  /** Current value to display */
  value?: string;
  /** Maximum number of digits */
  maxDigits?: number;
  /** Additional class names */
  className?: string;
}

// ============================================
// SEGMENT MAPPINGS
// ============================================

// Seven segments: a(top), b(top-right), c(bottom-right), d(bottom), e(bottom-left), f(top-left), g(middle)
//     aaa
//    f   b
//    f   b
//     ggg
//    e   c
//    e   c
//     ddd

const SEGMENT_MAP: Record<string, string[]> = {
  '0': ['a', 'b', 'c', 'd', 'e', 'f'],
  '1': ['b', 'c'],
  '2': ['a', 'b', 'g', 'e', 'd'],
  '3': ['a', 'b', 'g', 'c', 'd'],
  '4': ['f', 'g', 'b', 'c'],
  '5': ['a', 'f', 'g', 'c', 'd'],
  '6': ['a', 'f', 'g', 'e', 'c', 'd'],
  '7': ['a', 'b', 'c'],
  '8': ['a', 'b', 'c', 'd', 'e', 'f', 'g'],
  '9': ['a', 'b', 'c', 'd', 'f', 'g'],
};

const ALL_SEGMENTS = ['a', 'b', 'c', 'd', 'e', 'f', 'g'];

// ============================================
// SEVEN SEGMENT DIGIT COMPONENT
// ============================================

interface SevenSegmentDigitProps {
  digit?: string;
  isEmpty?: boolean;
}

function SevenSegmentDigit({ digit, isEmpty = false }: SevenSegmentDigitProps) {
  const activeSegments = digit ? SEGMENT_MAP[digit] || [] : [];

  return (
    <div className="seven-segment">
      {ALL_SEGMENTS.map((segment) => (
        <div
          key={segment}
          className={cn(
            'seven-segment__bar',
            `seven-segment__bar--${segment}`,
            activeSegments.includes(segment) && !isEmpty
              ? 'seven-segment__bar--active'
              : 'seven-segment__bar--inactive'
          )}
        />
      ))}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function CalculatorDisplay({
  value = '',
  maxDigits = 6,
  className,
}: CalculatorDisplayProps) {
  // Pad value with empty slots on the left
  const displayValue = value.slice(0, maxDigits).padStart(maxDigits, ' ');
  const digits = displayValue.split('');

  return (
    <div className={cn('calculator-display', className)}>
      {/* Digit slots */}
      <div className="calculator-display__digits">
        {digits.map((digit, index) => (
          <SevenSegmentDigit
            key={index}
            digit={digit}
            isEmpty={digit === ' '}
          />
        ))}
      </div>

      {/* Separator line */}
      <div className="calculator-display__separator" />

      {/* AZN Currency Symbol */}
      <div className="calculator-display__currency">
        <div className="azn-symbol">
          {/* Left vertical */}
          <div className="azn-symbol__bar azn-symbol__bar--left" />
          {/* Right vertical */}
          <div className="azn-symbol__bar azn-symbol__bar--right" />
          {/* Top horizontal bar */}
          <div className="azn-symbol__bar azn-symbol__bar--top" />
          {/* Vertical strike through top */}
          <div className="azn-symbol__bar azn-symbol__bar--top-strike" />
          {/* Bottom strike-through */}
          <div className="azn-symbol__bar azn-symbol__bar--bottom-strike" />
        </div>
      </div>
    </div>
  );
}
